// Pure dashboard aggregation. CommonJS so it runs under plain `node` (repo has no
// test runner). Vue imports named exports via Babel interop. Reuses budget.js.
const {
  HQ_LINE_CATEGORIES,
  EXPENSE_CATEGORIES,
  DISTRICT_CATEGORIES,
  DISTRICT_EXPENSE_CATEGORIES,
  CATEGORY_TO_GROUPTYPE,
  CATEGORY_LABELS,
  getAmount,
  caseActual,
  isDistrictExpenseCategory,
} = require("./budget");

// ---------- Status ----------

// Status keys + labels per case type, in display order. "rejected" applies to all.
const STATUS_DEFS = {
  kjp: [
    ["site_visit", "Site visit"],
    ["nod", "NOD / BQ"],
    ["work_order", "Work order"],
    ["completed", "Completed"],
    ["rejected", "Rejected"],
  ],
  emergency: [
    ["site_visit", "Site visit"],
    ["claims", "Claims"],
    ["approved", "Approved"],
    ["rejected", "Rejected"],
  ],
  electrical: [
    ["site_visit", "Site visit"],
    ["claims", "Claims"],
    ["approved", "Approved"],
    ["rejected", "Rejected"],
  ],
};

const TYPE_LABELS = { kjp: "NOD", emergency: "Emergency", electrical: "Electrical" };

// Map a case's groupType (stored PascalCase, e.g. "KJP") to a type key.
function caseTypeKey(c) {
  const g = String((c && c.groupType) || "").toLowerCase();
  if (g.includes("kjp")) return "kjp";
  if (g.includes("emergency")) return "emergency";
  if (g.includes("electrical")) return "electrical";
  return null;
}

// Which status bucket a case currently sits in. Mirrors Dashboard.vue's branch
// order but counts correctly (Dashboard's counter has an off-by-one: it seeds the
// first case in each bucket to 0 instead of 1).
function caseStatusKey(c) {
  if (!c) return null;
  const type = caseTypeKey(c);
  if (!type) return null;
  if (c.isRejected) return "rejected";
  const s = c.componentStatus || {};
  if (s.site_visit !== true) return "site_visit";
  if (type === "kjp") {
    if (s.bq && (!s.memo || !s.claim)) return "work_order";
    if (!s.bq) return "nod";
    if (s.claim && s.memo) return "completed";
    // unreachable for boolean bq/memo/claim — defensive default
    return null;
  }
  // emergency / electrical
  if (!s.bq) return "claims";
  return "approved";
}

// Count cases by [type][statusKey], optionally filtered to one district.
function statusCounts(cases, { districtId } = {}) {
  const out = { kjp: {}, emergency: {}, electrical: {} };
  (cases || []).forEach((c) => {
    if (districtId != null && c.district !== districtId) return;
    const type = caseTypeKey(c);
    const key = caseStatusKey(c);
    if (!type || !key) return;
    out[type][key] = (out[type][key] || 0) + 1;
  });
  return out;
}

// Headline counts across the (optionally district-filtered) cases.
function statusKpis(cases, { districtId } = {}) {
  let total = 0,
    done = 0,
    rejected = 0;
  (cases || []).forEach((c) => {
    if (districtId != null && c.district !== districtId) return;
    const key = caseStatusKey(c);
    if (!key) return;
    total += 1;
    if (key === "completed" || key === "approved") done += 1;
    else if (key === "rejected") rejected += 1;
  });
  return { total, done, rejected, inProgress: total - done - rejected };
}

// ---------- Budget scope helpers ----------

// budget column name on nod-budgets equals the groupType value for breakable cats.
const CATEGORY_TO_BUDGET_COLUMN = CATEGORY_TO_GROUPTYPE;

function isManualCategory(category) {
  return EXPENSE_CATEGORIES.indexOf(category) !== -1;
}
function isBreakableCategory(category) {
  return DISTRICT_CATEGORIES.indexOf(category) !== -1;
}

function isAllDistricts(districtId) {
  return districtId == null || districtId === "all";
}

// Sentinel for the "legacy, no district" bucket on district-expense
// categories. Must NOT be null/"all" — isAllDistricts() already treats those
// as "every district summed together", which is a different meaning.
const UNASSIGNED_DISTRICT_ID = "unassigned";

// Yearly allocation for a category at the given scope.
// All districts -> HQ ceiling (nod-budget-lines). Specific district -> that
// district's nod-budgets column (breakable only; manual cats -> null).
function allocationForCategory(category, { lines, budgets, districtId }) {
  if (isAllDistricts(districtId)) return getAmount(lines, category);
  if (!isBreakableCategory(category)) return null;
  const col = CATEGORY_TO_BUDGET_COLUMN[category];
  return (budgets || []).reduce(
    (sum, b) => sum + (b.district && b.district.id === districtId ? Number(b[col]) || 0 : 0),
    0
  );
}

// Sum expense rows matching category/year/month, optionally further filtered
// by matchDistrict(e) -> boolean.
function sumExpenses(expenses, category, year, month, matchDistrict) {
  return (expenses || []).reduce((sum, e) => {
    if (e.category !== category) return sum;
    if (e.year != null && Number(e.year) !== year) return sum;
    if (month != null && Number(e.month) !== month) return sum;
    if (matchDistrict && !matchDistrict(e)) return sum;
    return sum + (Number(e.amount) || 0);
  }, 0);
}

// Used amount for a category at a scope and (optional) month.
// Breakable -> from cases (caseActual). Manual -> from expenses (no district
// dim, except DISTRICT_EXPENSE_CATEGORIES which split by expense.district;
// district on an expense row is a populated relation object or null/absent).
function usedForCategory(category, { cases, expenses, districtId, year, month, moment }) {
  const all = isAllDistricts(districtId);
  if (isManualCategory(category)) {
    if (!all) {
      if (!isDistrictExpenseCategory(category)) return null;
      const matchDistrict = (e) => {
        const eDistrictId = e.district && e.district.id != null ? e.district.id : null;
        return districtId === UNASSIGNED_DISTRICT_ID
          ? eDistrictId == null
          : eDistrictId === districtId;
      };
      return sumExpenses(expenses, category, year, month, matchDistrict);
    }
    return sumExpenses(expenses, category, year, month);
  }
  if (isBreakableCategory(category)) {
    if (!all) return caseActual(cases, { category, districtId, year, month, moment });
    const districtIds = Array.from(new Set((cases || []).map((c) => c.district)));
    return districtIds.reduce(
      (sum, did) => sum + caseActual(cases, { category, districtId: did, year, month, moment }),
      0
    );
  }
  return 0;
}

// ---------- Budget views ----------

// "top" rows (non-WPS) vs "wps" group rows.
const TOP_CATEGORIES = ["kos_awalan", "kerja_rutin"];
const WPS_CATEGORIES = ["wps_nod", "wps_emergency", "wps_electrical", "wps_pca", "wps_irms", "wps_tot"];

function emptyAgg() {
  return { allocated: 0, used: 0, remaining: 0 };
}
function addAgg(agg, row) {
  agg.allocated += row.allocated || 0;
  agg.used += row.used || 0;
  agg.remaining += row.remaining || 0;
  return agg;
}

function budgetRow(category, scope) {
  const allocated = allocationForCategory(category, scope);
  const used = usedForCategory(category, scope) || 0;
  return {
    category,
    label: CATEGORY_LABELS[category],
    group: WPS_CATEGORIES.indexOf(category) !== -1 ? "wps" : "top",
    allocated,
    used,
    remaining: allocated == null ? null : allocated - used,
  };
}

// Full-year category breakdown. All districts -> all 8 cats; specific district ->
// breakable cats only. Returns rows + WPS subtotal + grand total.
function budgetCategoryView(scope) {
  const cats = isAllDistricts(scope.districtId)
    ? TOP_CATEGORIES.concat(WPS_CATEGORIES)
    : DISTRICT_CATEGORIES.concat(DISTRICT_EXPENSE_CATEGORIES);
  const rows = cats.map((c) => budgetRow(c, scope));
  const wpsSubtotal = rows
    .filter((r) => r.group === "wps")
    .reduce(addAgg, emptyAgg());
  // Categories with no allocation ceiling at this scope (e.g. kerja_rutin at
  // a specific district) are excluded from the grand total — their `used`
  // would otherwise inflate Total Used without a matching ceiling to draw
  // down, breaking "Allocated - Used = Remaining". They still appear in
  // `rows` with their own real figures, just don't feed the aggregate.
  const total = rows.filter((r) => r.allocated != null).reduce(addAgg, emptyAgg());
  return { rows, wpsSubtotal, total };
}

// Districts list for a district-expense category's breakdown, with a
// synthetic "Unassigned" row appended when there's non-zero legacy data
// (expense rows with no district) for that category+year. Omitted otherwise
// so it doesn't linger once everything has a real district.
function districtsWithUnassigned(districts, expenses, category, year) {
  const hasUnassigned = (expenses || []).some((e) => {
    if (e.category !== category) return false;
    if (year != null && Number(e.year) !== year) return false;
    const eDistrictId = e.district && e.district.id != null ? e.district.id : null;
    if (eDistrictId != null) return false;
    return (Number(e.amount) || 0) !== 0;
  });
  const list = (districts || []).slice();
  if (hasUnassigned) list.push({ id: UNASSIGNED_DISTRICT_ID, name: "Unassigned" });
  return list;
}

// Full-year, single category, one row per district. `districts` is the
// district list [{id,name}]; rows preserve that order. `allocated` (and thus
// `remaining`) stays null when the category has no per-district ceiling
// (e.g. a district-expense category like kerja_rutin) — callers render "—"
// for that, not a misleading 0/negative number. `total` is a raw sum across
// rows and is not meaningful for null-allocation categories; callers must not
// render `.total` for those (the HQ-level totals come from budgetRow/
// budgetCategoryView instead).
function budgetDistrictView(scope, category, districts) {
  const rows = (districts || []).map((d) => {
    const dScope = Object.assign({}, scope, { districtId: d.id });
    const allocated = allocationForCategory(category, dScope);
    const used = usedForCategory(category, dScope) || 0;
    return {
      districtId: d.id,
      label: d.name,
      allocated,
      used,
      remaining: allocated == null ? null : allocated - used,
    };
  });
  const total = rows.reduce(addAgg, emptyAgg());
  return { rows, total };
}

// Months 1..12 used amount for one (category, scope). Returns a length-12 array
// indexed 0..11. Allocation has no month dimension, so it is not split here.
function monthlyUsed(category, scope) {
  const months = new Array(12).fill(0);
  for (let m = 1; m <= 12; m++) {
    months[m - 1] = usedForCategory(category, Object.assign({}, scope, { month: m })) || 0;
  }
  return months;
}

function toCumulative(months) {
  let running = 0;
  return months.map((v) => (running += v));
}

// Month matrix. rowDim "category": rows = categories in scope. rowDim "district":
// rows = districts for the single `category`. `cumulative` converts months to a
// running total. Returns rows (each with months[12], used, allocated, remaining),
// per-month column totals, and a grand total.
function budgetMonthMatrix(scope, { rowDim, category, districts, cumulative } = {}) {
  let rows;
  if (rowDim === "district") {
    rows = (districts || []).map((d) => {
      const dScope = Object.assign({}, scope, { districtId: d.id });
      const months = monthlyUsed(category, dScope);
      const allocated = allocationForCategory(category, dScope);
      const used = months.reduce((s, v) => s + v, 0);
      return {
        key: d.id,
        label: d.name,
        months,
        allocated,
        used,
        remaining: allocated == null ? null : allocated - used,
      };
    });
  } else {
    const cats = isAllDistricts(scope.districtId)
      ? TOP_CATEGORIES.concat(WPS_CATEGORIES)
      : DISTRICT_CATEGORIES.concat(DISTRICT_EXPENSE_CATEGORIES);
    rows = cats.map((c) => {
      const months = monthlyUsed(c, scope);
      const allocated = allocationForCategory(c, scope);
      const used = months.reduce((s, v) => s + v, 0);
      return {
        key: c,
        label: CATEGORY_LABELS[c],
        months,
        allocated,
        used,
        remaining: allocated == null ? null : allocated - used,
      };
    });
  }

  // Column totals from the raw (non-cumulative) months. Rows with no
  // allocation ceiling at this scope are excluded, same rule as `total`
  // below, so both stay reconciled (mirrors budgetCategoryView's total).
  const monthTotals = new Array(12).fill(0);
  rows
    .filter((r) => r.allocated != null)
    .forEach((r) => r.months.forEach((v, i) => (monthTotals[i] += v)));

  if (cumulative) {
    rows.forEach((r) => (r.months = toCumulative(r.months)));
  }
  const finalMonthTotals = cumulative ? toCumulative(monthTotals) : monthTotals;

  const total = rows.filter((r) => r.allocated != null).reduce(
    (agg, r) => {
      agg.allocated += r.allocated;
      agg.used += r.used;
      agg.remaining += r.remaining;
      return agg;
    },
    { allocated: 0, used: 0, remaining: 0 }
  );

  return { rows, monthTotals: finalMonthTotals, total };
}

const STATUS_TYPES = ["kjp", "emergency", "electrical"];

// Set of status keys applicable to a type (for N/A "—" cells).
function statusKeysFor(type) {
  return STATUS_DEFS[type].map((d) => d[0]);
}

// Status (rows) x type (columns) view. Cells are counts, or null when the status
// does not apply to that type. Rows = union of all statuses in display order.
function statusTypeView(cases, { districtId } = {}) {
  const counts = statusCounts(cases, { districtId });
  // union of status keys, ordered: kjp's order then any extra from others.
  const order = [];
  STATUS_TYPES.forEach((t) =>
    statusKeysFor(t).forEach((k) => {
      if (order.indexOf(k) === -1) order.push(k);
    })
  );
  const labelOf = {};
  STATUS_TYPES.forEach((t) => STATUS_DEFS[t].forEach(([k, l]) => (labelOf[k] = l)));

  const rows = order.map((k) => {
    const cells = {};
    STATUS_TYPES.forEach((t) => {
      cells[t] = statusKeysFor(t).indexOf(k) === -1 ? null : counts[t][k] || 0;
    });
    return { key: k, label: labelOf[k], cells };
  });

  const totals = {};
  let grandTotal = 0;
  STATUS_TYPES.forEach((t) => {
    totals[t] = Object.keys(counts[t]).reduce((s, k) => s + counts[t][k], 0);
    grandTotal += totals[t];
  });

  return { rows, totals, grandTotal, types: STATUS_TYPES };
}

// District (rows) x status (columns) view for a single type.
function statusDistrictView(cases, type, districts) {
  const columns = STATUS_DEFS[type].slice(); // [[key,label],...]
  const keys = columns.map((c) => c[0]);
  const rows = (districts || []).map((d) => {
    const counts = statusCounts(cases, { districtId: d.id })[type];
    const cells = {};
    let total = 0;
    keys.forEach((k) => {
      cells[k] = counts[k] || 0;
      total += cells[k];
    });
    return { key: d.id, label: d.name, cells, total };
  });
  return { columns, rows };
}

module.exports = {
  STATUS_DEFS,
  TYPE_LABELS,
  caseTypeKey,
  caseStatusKey,
  statusCounts,
  statusKpis,
  CATEGORY_TO_BUDGET_COLUMN,
  isManualCategory,
  isBreakableCategory,
  isAllDistricts,
  UNASSIGNED_DISTRICT_ID,
  allocationForCategory,
  usedForCategory,
  TOP_CATEGORIES,
  WPS_CATEGORIES,
  budgetCategoryView,
  budgetDistrictView,
  districtsWithUnassigned,
  monthlyUsed,
  budgetMonthMatrix,
  STATUS_TYPES,
  statusKeysFor,
  statusTypeView,
  statusDistrictView,
};
